import { useCallback, useState } from 'react';
import { useGetConversationHistoryLazyQuery } from '@gonfalon/observability-graphql/hooks';

import { type ConversationData, useAgentInsights } from '../AgentInsightsContext';

/**
 * Fetch a saved conversation's history and load it into the provider.
 *
 * Lives here rather than in `AgentInsightsPanel` because a conversation id alone isn't enough to
 * render a thread: `configureConversation({ conversationIdToLoad })` only puts the id in context,
 * and the messages have to be fetched. Any host that mounts `VegaConversation` outside the drawer
 * needs this, or a resumed conversation comes up empty.
 */
export function useRetrieveConversation() {
  const { loadConversation } = useAgentInsights();
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const [conversationNotShared, setConversationNotShared] = useState(false);
  const [getConversationHistory] = useGetConversationHistoryLazyQuery();

  const retrieveConversation = useCallback(
    async (
      conversationId: string,
      title?: string | null,
      /**
       * Checked just before the history is applied. A host that has moved on — unmounted, or the
       * member discarded the thread while it was in flight — uses this to stop a late response
       * putting the old conversation back.
       */
      options?: { isCurrent?: () => boolean },
    ) => {
      setIsLoadingConversation(true);
      setConversationNotShared(false);
      return await runWithCleanup(
        async () => {
          let __ldFailure: unknown;
          try {
            __ldAttempt: {
              const { data, error } = await getConversationHistory({
                variables: { conversation_id: conversationId },
                fetchPolicy: 'network-only',
              });

              if (error) {
                if (error.message?.includes('not been shared')) {
                  setConversationNotShared(true);
                }
                __ldFailure = error;
                break __ldAttempt;
              }

              const response = data?.conversation_history;
              const messages = response?.messages || [];

              if (messages.length > 0 && options?.isCurrent?.() !== false) {
                const conversationData: ConversationData = {
                  conversationId,
                  title,
                  status: response?.status || 'active',
                  error: response?.error,
                  messages,
                };

                loadConversation(conversationData);
              }

              return messages;
            }
          } catch (error: unknown) {
            __ldFailure = error;
          }
          console.error('Error retrieving conversation history:', __ldFailure);
          throw __ldFailure;
        },
        () => {
          setIsLoadingConversation(false);
        },
      );
    },
    [getConversationHistory, loadConversation],
  );

  const resetConversationNotShared = useCallback(() => {
    setConversationNotShared(false);
  }, []);

  return { retrieveConversation, isLoadingConversation, conversationNotShared, resetConversationNotShared };
}

function runWithCleanup<T>(run: () => Promise<T>, cleanup: () => void): Promise<T> {
  return run().then(
    (value) => {
      cleanup();
      return value;
    },
    (error) => {
      cleanup();
      throw error;
    },
  );
}
